import { stableSerialize } from "@erp/shared-utils";
import type { EdgeDefinition, StepDefinition } from "../types";
import { remapWorkflowExpressionStepIds } from "../expressions";
import { remapManualExpressionOverrides } from "../portability";
import { remapActionBoundaryPorts } from "./action-interfaces";

type StepIdentity = { key: string; label: string; name: string };
type StepDetails = {
  type: StepDefinition["type"];
  sockets: {
    inputs: StepDefinition["sockets"]["inputs"];
    outputs: Pick<
      StepDefinition["sockets"]["outputs"][number],
      "id" | "type"
    >[];
  };
  position?: StepDefinition["position"];
};
type MatchStep = Pick<StepDefinition, "id" | "name" | "config"> & {
  base: string;
  content: string;
  position: StepDefinition["position"];
};

function compareText(left: string, right: string): number {
  return left === right ? 0 : left < right ? -1 : 1;
}

function compareSteps(left: MatchStep, right: MatchStep): number {
  return (
    compareText(left.content, right.content) ||
    left.position.x - right.position.x ||
    left.position.y - right.position.y ||
    compareText(left.id, right.id)
  );
}

function matchingStep<
  Step extends Pick<StepDefinition, "id" | "name" | "config">,
>(step: Step, details: StepDetails, stepIds: Map<string, string>): MatchStep {
  const [boundary] = remapActionBoundaryPorts(
    [{ type: details.type, config: step.config }],
    stepIds,
  );
  const config = remapManualExpressionOverrides(boundary.config, stepIds);
  const inputs = details.sockets.inputs.map((socket) => ({
    id: socket.id,
    type: socket.type,
    required: socket.required,
    expression: socket.manualOverride
      ? normalizeMatchingExpression(socket.expression, stepIds)
      : undefined,
  }));
  const outputs = details.sockets.outputs.map(({ id, type }) => ({ id, type }));
  return {
    id: step.id,
    name: step.name,
    config,
    base: `${details.type}::${step.name}`,
    content: stableSerialize([config, inputs, outputs]),
    position: details.position ?? { x: 0, y: 0 },
  };
}

function normalizeMatchingExpression(
  expression: string | undefined,
  stepIds: ReadonlyMap<string, string>,
): string | undefined {
  if (expression === undefined) return expression;
  try {
    return remapWorkflowExpressionStepIds(expression, stepIds);
  } catch {
    return expression;
  }
}

function matchingSteps<
  Step extends Pick<StepDefinition, "id" | "name" | "config">,
>(
  steps: readonly Step[],
  stepDetails: (step: Step) => StepDetails,
  identities?: ReadonlyMap<string, StepIdentity>,
) {
  const entries = steps.map((step) => ({ step, details: stepDetails(step) }));
  // Start with type/name references, then use matched occurrence identities
  // so draft UUIDs cannot decide duplicate matches before content is scored.
  const stepIds = new Map(
    entries.map(({ step, details }) => [
      step.id,
      identities?.get(step.id)?.key ?? `${details.type}::${step.name}`,
    ]),
  );
  return entries.map(({ step, details }) =>
    matchingStep(step, details, stepIds),
  );
}

function sameStepIdentityKeys(
  left: ReadonlyMap<string, StepIdentity>,
  right: ReadonlyMap<string, StepIdentity>,
): boolean {
  return (
    left.size === right.size &&
    [...left].every(([id, identity]) => right.get(id)?.key === identity.key)
  );
}

function collidingStepNames(steps: readonly MatchStep[]): Set<string> {
  const basesByName = new Map<string, Set<string>>();
  for (const step of steps) {
    const bases = basesByName.get(step.name) ?? new Set<string>();
    bases.add(step.base);
    basesByName.set(step.name, bases);
  }
  return new Set(
    [...basesByName]
      .filter(([, bases]) => bases.size > 1)
      .map(([name]) => name),
  );
}

function pairSteps(before: readonly MatchStep[], after: readonly MatchStep[]) {
  const candidates = before.flatMap((left) =>
    after.map((right) => ({
      left,
      right,
      exact: Number(left.content === right.content),
      equalKeys: Object.keys(left.config).filter(
        (key) =>
          Object.prototype.hasOwnProperty.call(right.config, key) &&
          stableSerialize(left.config[key]) ===
            stableSerialize(right.config[key]),
      ).length,
      distance:
        (left.position.x - right.position.x) ** 2 +
        (left.position.y - right.position.y) ** 2,
    })),
  );
  candidates.sort(
    (a, b) =>
      b.exact - a.exact ||
      b.equalKeys - a.equalKeys ||
      a.distance - b.distance ||
      compareSteps(a.left, b.left) ||
      compareSteps(a.right, b.right),
  );
  const pairedBefore = new Set<string>();
  const pairs = new Map<string, MatchStep>();
  for (const { left, right } of candidates) {
    if (pairedBefore.has(left.id) || pairs.has(right.id)) continue;
    pairedBefore.add(left.id);
    pairs.set(right.id, left);
  }
  return pairs;
}

function assignStepIdentities(
  oldSteps: readonly MatchStep[],
  newSteps: readonly MatchStep[],
) {
  const bases = new Set([...oldSteps, ...newSteps].map((step) => step.base));
  const collidingNames = collidingStepNames([...oldSteps, ...newSteps]);
  const oldIdentities = new Map<string, StepIdentity>();
  const newIdentities = new Map<string, StepIdentity>();
  for (const base of [...bases].sort(compareText)) {
    const oldGroup = oldSteps
      .filter((step) => step.base === base)
      .sort(compareSteps);
    const newGroup = newSteps
      .filter((step) => step.base === base)
      .sort(compareSteps);
    const pairs = pairSteps(oldGroup, newGroup);
    let occurrence = 0;
    function nextIdentity(step: MatchStep): StepIdentity {
      while (occurrence > 0 && bases.has(`${base}#${occurrence}`)) occurrence++;
      const suffix = occurrence === 0 ? "" : `#${occurrence}`;
      occurrence++;
      const label = collidingNames.has(step.name) ? base : step.name;
      return {
        key: `${base}${suffix}`,
        label: `${label}${suffix}`,
        name: step.name,
      };
    }
    for (const step of oldGroup) oldIdentities.set(step.id, nextIdentity(step));
    for (const step of newGroup) {
      const old = pairs.get(step.id);
      const identity = old && oldIdentities.get(old.id);
      newIdentities.set(step.id, identity ?? nextIdentity(step));
    }
  }
  return { before: oldIdentities, after: newIdentities };
}

/** Match both graphs together: exact content first, then deterministic similarity. */
export function workflowDiffStepIdentities<
  Step extends Pick<StepDefinition, "id" | "name" | "config">,
>(
  before: readonly Step[],
  after: readonly Step[],
  stepDetails: (step: Step) => StepDetails,
) {
  let identities = assignStepIdentities(
    matchingSteps(before, stepDetails),
    matchingSteps(after, stepDetails),
  );
  // Propagate resolved occurrences through consumer chains, bounded by graph
  // size so even invalid cyclic references cannot keep the matcher running.
  for (let pass = 0; pass < Math.max(before.length, after.length); pass++) {
    const next = assignStepIdentities(
      matchingSteps(before, stepDetails, identities.before),
      matchingSteps(after, stepDetails, identities.after),
    );
    if (
      sameStepIdentityKeys(identities.before, next.before) &&
      sameStepIdentityKeys(identities.after, next.after)
    )
      return next;
    identities = next;
  }
  return identities;
}

function diffEndpoint(
  stepId: string,
  socketId: string,
  identities: ReadonlyMap<string, StepIdentity>,
) {
  const { key, name, label } = identities.get(stepId) ?? {
    key: stepId,
    name: stepId,
    label: stepId,
  };
  return { label: `${label}:${socketId}`, step: { key, name, socketId } };
}

export function workflowDiffEdges(
  edges: readonly Pick<
    EdgeDefinition,
    "sourceStepId" | "sourceSocketId" | "targetStepId" | "targetSocketId"
  >[],
  identities: ReadonlyMap<string, StepIdentity>,
) {
  const entries = edges.map((edge) => {
    const source = diffEndpoint(
      edge.sourceStepId,
      edge.sourceSocketId,
      identities,
    );
    const target = diffEndpoint(
      edge.targetStepId,
      edge.targetSocketId,
      identities,
    );
    const fromKey = `${source.step.key}:${edge.sourceSocketId}`;
    const toKey = `${target.step.key}:${edge.targetSocketId}`;
    return [
      `${fromKey} → ${toKey}`,
      {
        from: source.label,
        to: target.label,
        fromStep: source.step,
        toStep: target.step,
      },
    ] as const;
  });
  return new Map(entries.sort(([left], [right]) => compareText(left, right)));
}
